import React, { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { XCircleIcon, LifebuoyIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { supportApi } from '../services/api'

interface TicketDetailModalProps {
  ticketId: string
  isOpen: boolean
  onClose: () => void
  onUpdate?: () => void
}

interface Ticket {
  id: string
  ticketNumber: string
  subject: string
  description: string
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED'
  priority: 'LOW' | 'MEDIUM' | 'HIGH'
  response?: string
  createdAt: string
  updatedAt: string
  userEmail: string
  userName: string
}

interface ResponseForm {
  message: string
}

export default function TicketDetailModal({ ticketId, isOpen, onClose, onUpdate }: TicketDetailModalProps) {
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ResponseForm>()

  const handleClose = useCallback(() => {
    console.log('Closing modal...')
    reset()
    onClose()
  }, [reset, onClose])

  useEffect(() => {
    if (isOpen && ticketId) {
      loadTicketDetails()
    }
  }, [isOpen, ticketId])

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        console.log('Escape key pressed, closing modal')
        handleClose()
      }
    }

    if (isOpen) {
      console.log('Modal opened, adding event listeners')
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      console.log('Cleaning up modal event listeners')
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, handleClose])

  const loadTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await supportApi.getTicketById(ticketId);
      setTicket(response);
    } catch (error: any) {
      console.error('Error loading ticket details:', error);
      toast.error('Failed to load ticket details');
      // Close modal on error after a brief delay to show the error message
      setTimeout(() => {
        handleClose();
      }, 2000)
    } finally {
      setLoading(false);
    }
  }

  const onSubmitResponse = async (data: ResponseForm) => {
    if (!ticket) return
    
    try {
      setSubmitting(true)
      await supportApi.respondToTicket(ticket.id, data.message)
      toast.success('Response submitted successfully')
      reset()
      // Reload ticket to show updated responses
      await loadTicketDetails()
      if (onUpdate) onUpdate()
    } catch (error: any) {
      console.error('Error submitting response:', error)
      toast.error(error.response?.data?.error || 'Failed to submit response')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
      case 'RESOLVED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
      case 'LOW':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
    }
  }

  const formatResponses = (responseText?: string) => {
    if (!responseText) return []
    
    // Split by response delimiters - look for the pattern "--- [Author] at [timestamp] ---"
    const responseParts = responseText.split(/(?=--- (?:Client|Admin|Mgmt|Employee) at .+? ---)/g).filter(text => text.trim())
    
    return responseParts.map((response, index) => {
      const lines = response.split('\n')
      const firstLine = lines[0]
      
      // Check if first line contains author and timestamp info
      const authorMatch = firstLine.match(/--- (Client|Admin|Mgmt|Employee) at (.+?) ---/)
      
      if (authorMatch) {
        return {
          author: authorMatch[1],
          timestamp: authorMatch[2],
          content: lines.slice(1).join('\n').trim()
        }
      } else {
        // If no author/timestamp pattern, treat as regular content
        return {
          author: index === 0 ? 'Original' : 'Unknown',
          timestamp: '',
          content: response.trim()
        }
      }
    })
  }

  const canRespond = ticket && (ticket.status === 'OPEN' || ticket.status === 'IN_PROGRESS')

  if (!isOpen) {
    console.log('Modal not open, returning null')
    return null
  }

  console.log('Rendering modal for ticket:', ticketId, 'isOpen:', isOpen)

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={(e) => {
        // Close modal when clicking backdrop
        console.log('Backdrop clicked', e.target === e.currentTarget)
        if (e.target === e.currentTarget) {
          console.log('Closing modal via backdrop click')
          handleClose()
        }
      }}
    >
      <div 
        className="bg-white dark:bg-tech-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Emergency close button at the top */}
        <div className="flex justify-end p-2 border-b border-gray-200 dark:border-tech-700">
          <button
            onClick={() => {
              console.log('EMERGENCY CLOSE button clicked')
              onClose() // Direct call to onClose, bypassing any potential issues
            }}
            className="text-red-500 hover:text-red-700 font-semibold text-sm px-2 py-1 border border-red-300 rounded"
            title="Force close modal"
          >
            ✕ CLOSE
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-tech-400">Loading ticket details...</p>
            <button
              onClick={handleClose}
              className="mt-4 px-4 py-2 text-gray-600 dark:text-tech-400 border border-gray-300 dark:border-tech-700 rounded-lg hover:bg-gray-50 dark:hover:bg-tech-800"
            >
              Cancel
            </button>
          </div>
        ) : ticket ? (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <LifebuoyIcon className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-tech-50">
                    {ticket.subject}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-tech-400 mt-1">
                    Ticket #{ticket.ticketNumber} • Created {formatDate(ticket.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Status and Priority */}
            <div className="flex items-center space-x-3 mb-6">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ticket.status)}`}>
                {ticket.status}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                {ticket.priority} Priority
              </span>
            </div>

            {/* Original Description */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-tech-50 mb-3">Description</h4>
              <div className="p-4 bg-gray-50 dark:bg-tech-800 rounded-lg">
                <p className="text-gray-700 dark:text-tech-300 whitespace-pre-wrap">
                  {ticket.description}
                </p>
              </div>
            </div>

            {/* Responses */}
            {ticket.response && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-tech-50 mb-3">Conversation</h4>
                <div className="space-y-4">
                  {formatResponses(ticket.response).map((response, index) => (
                    <div key={index} className="p-4 bg-gray-50 dark:bg-tech-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          response.author === 'Client' 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                            : response.author === 'Employee'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                            : response.author === 'Admin'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            : response.author === 'Mgmt'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {response.author}
                        </span>
                        {response.timestamp && (
                          <span className="text-xs text-gray-500 dark:text-tech-500">
                            {response.timestamp}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 dark:text-tech-300 whitespace-pre-wrap">
                        {response.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Response Form */}
            {canRespond ? (
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-tech-50 mb-3">Add Response</h4>
                <form onSubmit={handleSubmit(onSubmitResponse)} className="space-y-4">
                  <div>
                    <textarea
                      {...register('message', { 
                        required: 'Response message is required',
                        minLength: { value: 1, message: 'Message cannot be empty' }
                      })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-tech-800 dark:text-tech-100"
                      placeholder="Type your response here..."
                    />
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-gray-600 dark:text-tech-400 border border-gray-300 dark:border-tech-700 rounded-lg hover:bg-gray-50 dark:hover:bg-tech-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || submitting}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                    >
                      {(isSubmitting || submitting) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <PaperAirplaneIcon className="w-4 h-4" />
                      )}
                      <span>Send Response</span>
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 dark:bg-tech-800 rounded-lg text-center">
                <p className="text-gray-600 dark:text-tech-400">
                  This ticket is {ticket.status.toLowerCase()} and cannot receive new responses.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="flex justify-end mb-4">
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-tech-300"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>
            <p className="text-red-500 mb-4">Failed to load ticket details</p>
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 dark:text-tech-400 border border-gray-300 dark:border-tech-700 rounded-lg hover:bg-gray-50 dark:hover:bg-tech-800"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
